/**
 * True when the quote progress clock should advance.
 * `running` is already false for reduced motion, a hidden tab, or an offscreen card.
 * Hover and focus pause the clock. A manual choice can ignore the hover or focus
 * that the choice itself caused until that pointer or focus leaves.
 */
export function quoteAutoplayRuns({running, hovered, focused, ignoreHover = false, ignoreFocus = false}) {
    if (!running) return false;
    if (hovered && !ignoreHover) return false;
    if (focused && !ignoreFocus) return false;
    return true;
}

/**
 * Every quote change restarts the clock at zero.
 * A manual change keeps the clock running through the hover and focus that
 * the control just took. An automatic change keeps a hold that is already set.
 */
export function quoteClockAfterChange({automatic, hovered, focused, ignoreHover = false, ignoreFocus = false}) {
    return {
        restart: true,
        ignoreHover: automatic ? ignoreHover : Boolean(hovered),
        ignoreFocus: automatic ? ignoreFocus : Boolean(focused),
    };
}

/** Drop a manual-navigation hold once that hover or focus has ended. */
export function releaseQuoteNavHold({ignoreHover, ignoreFocus, hovered, focused}) {
    return {
        ignoreHover: hovered ? ignoreHover : false,
        ignoreFocus: focused ? ignoreFocus : false,
    };
}

/** Plan replacements together so persistent logos always retain a visible slot. */
export function planLogoChange(cells, logos, index, turn, random = Math.random) {
    const cell = cells[index];
    if (cell.busy || cell.logo === undefined) return [];
    const occupied = new Set(cells.flatMap((item) => [item.logo, item.incoming]));
    if (!logos.find((logo) => logo.id === cell.logo).persistent) {
        for (let offset = 0; offset < logos.length; offset++) {
            const candidate = logos[(turn + offset) % logos.length];
            if (!occupied.has(candidate.id)) return [{index, logo: candidate.id}];
        }
    }

    // A paired swap moves a persistent logo without removing it from the wall.
    const destinations = cells.map((item, destination) => destination)
        .filter((destination) => destination !== index && !cells[destination].busy && cells[destination].logo !== undefined);
    if (!destinations.length) return [];
    const destination = destinations[Math.floor(random() * destinations.length)];
    return [{index, logo: cells[destination].logo}, {index: destination, logo: cell.logo}];
}

/** Change at most two cells per beat, keeping paired persistent-logo swaps intact. */
export function planLogoBatch(cells, logos, index, turn, random = Math.random) {
    const reserved = cells.map((cell) => ({...cell}));
    const changes = [];
    for (let offset = 0; offset < cells.length && changes.length < 2; offset++) {
        const plan = planLogoChange(reserved, logos, (index + offset) % cells.length, turn, random);
        if (changes.length + plan.length > 2) continue;
        for (const change of plan) {
            reserved[change.index].busy = true;
            reserved[change.index].incoming = change.logo;
            changes.push(change);
        }
    }
    return changes;
}
